import React from 'react';
import { TrendingDown, FileCheck, Zap, Award, Brain, Database, Shield, Layers, ArrowRight } from 'lucide-react';
import './Products.css';

const Products = () => {
  const benefits = [
    { icon: <TrendingDown size={24} />, text: 'Reduce risk with precision-driven insights' },
    { icon: <FileCheck size={24} />, text: 'Ensure regulatory compliance automatically' },
    { icon: <Zap size={24} />, text: 'Streamline complex analysis workflows' },
    { icon: <Award size={24} />, text: 'Make confident, high-impact decisions faster' }
  ];

  const features = [
    {
      icon: <Brain size={40} />,
      title: 'Cognitive Reasoning',
      description: 'Advanced reasoning engine that validates and surfaces truth grounded in data—no hallucinations, no speculation.'
    },
    {
      icon: <Database size={40} />,
      title: 'AI Stem Cells',
      description: 'Adaptive technology that enables domain-specific intelligence without compromising accuracy or transparency.'
    },
    {
      icon: <Shield size={40} />,
      title: 'Defensible Insights',
      description: 'Every insight is traceable, explainable, and defensible—built for high-stakes environments.'
    },
    {
      icon: <Layers size={40} />,
      title: 'Enterprise Integration',
      description: 'Seamlessly integrates with existing systems and workflows for immediate value delivery.'
    }
  ];

  const useCases = [
    { industry: 'Finance', use: 'Risk assessment, fraud detection, regulatory compliance' },
    { industry: 'Healthcare', use: 'Operations optimization, supply chain, patient outcomes' },
    { industry: 'Legal', use: 'Due diligence, compliance verification, case analysis' },
    { industry: 'Government', use: 'National security, policy analysis, resource allocation' }
  ];

  return (
    <div className="products-page">
      <section className="products-hero">
        <div className="products-hero-content">
          <p className="products-hero-label">Flagship Product</p>
          <h1 className="products-hero-title">Atai™ Cognitive AI Platform</h1>
          <p className="products-hero-subtitle">
            Transform complex datasets into precise, actionable intelligence. 
            Uncover Absolute Truth with the power of Cognitive AI.
          </p>
          <div className="products-hero-buttons">
            <button className="hero-btn primary">Request Demo</button>
            <button className="hero-btn secondary">Watch Overview</button>
          </div>
        </div>
        <div className="products-hero-visual">
          <div className="atai-graphic">
            <div className="graphic-ring outer"></div>
            <div className="graphic-ring middle"></div>
            <div className="graphic-ring inner"></div>
            <div className="graphic-core">
              <span className="core-text">Atai™</span>
            </div>
          </div>
        </div>
      </section>

      <section className="products-benefits">
        <div className="benefits-container">
          <h2 className="benefits-title">Empower Your Decision-Making</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <div className="benefit-icon">{benefit.icon}</div>
                <span className="benefit-text">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="products-features">
        <div className="features-container">
          <p className="features-label">Platform Capabilities</p>
          <h2 className="features-title">Built for Precision, Designed for Truth</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="products-how">
        <div className="how-container">
          <p className="how-label">How It Works</p>
          <h2 className="how-title">From Data to Truth in Three Steps</h2>
          <div className="how-steps">
            <div className="how-step">
              <div className="step-number">01</div>
              <h3>Ingest</h3>
              <p>Connect your data sources. Atai™ ingests and normalizes complex, interconnected datasets.</p>
            </div>
            <div className="how-connector"></div>
            <div className="how-step">
              <div className="step-number">02</div>
              <h3>Analyze</h3>
              <p>Cognitive AI reasons through your data, validating patterns and surfacing hidden insights.</p>
            </div>
            <div className="how-connector"></div>
            <div className="how-step">
              <div className="step-number">03</div>
              <h3>Decide</h3>
              <p>Receive precise, defensible intelligence that empowers confident decision-making.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="products-usecases">
        <div className="usecases-container">
          <p className="usecases-label">Use Cases</p>
          <h2 className="usecases-title">Atai™ Across Industries</h2>
          <div className="usecases-grid">
            {useCases.map((item, index) => (
              <div key={index} className="usecase-card">
                <h3>{item.industry}</h3>
                <p>{item.use}</p>
                <button className="usecase-link">
                  Learn More <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="products-cta">
        <div className="cta-container">
          <h2>Ready to Experience Absolute Truth?</h2>
          <p>Schedule a personalized demo and see how Atai™ can transform your decision-making.</p>
          <button className="cta-button">Request Demo</button>
        </div>
      </section>
    </div>
  );
};

export default Products;
